import moment from 'moment';
import { tl } from 'common/services/i18nService';
import { groupBy } from 'lodash';
import { isNullOrUndefined } from 'common/util/utils';
import { CommentType } from '../reducer';
import { urnService } from './urnService';
import { QuickCommentsAppliedInfo, QuickMarkOrComment } from '../../../store/common/features/types';
import store from '../../../store';
import { RootState } from '../../../store/rootReducer';
import { Committee } from '../../../services/committeeService';
import { Evaluator } from '../../../store/assessment/reducer';
import xmlParser from '../../../services/xmlParser';
import { BandedCriterion, Criterion } from '../../setMark/reducer';

export interface CustomQuickComment {
    quickMarkId: number;
    criteriaText: string;
    evaluatorId: number;
    questionNumber: string;
    group: string;
    isQuickComment: boolean;
    questionTitle: string;
    criteriaScore?: number;
    questionId?: number;
}

export interface CriterionAppliedByInfo {
    criteriaId: string;
    evaluatorId: number;
    rewardMarks: number;
    rewardPercentage: number;
    criterionFeedback?: string;
}

export interface CriteriaComment {
    type: string;
    questionId: string;
    questionNumber: string;
    criteria: any[];
    appliedBy?: CriterionAppliedByInfo[];
    group: string;
    appliedByEvaluatorId?: string;
}

export interface AlternativeOrQuestionFeedback {
    type: string;
    questionId: string;
    questionNumber: string;
    feedback: string;
    group: string;
    selectedAlternative?: boolean;
    alternativeIdentifier? : string;
}

export interface InteractionFeedback {
    type: string;
    questionId: string;
    questionNumber: string;
    feedback: string;
    group: string;
    outcomeIdentifier? : string;
    responseOrderIndex: number;
}

export enum CommentTypes {
    BANDED_MARKING = 'banded_marking',
    CRITERIA_MARKING ='criteria_marking',
    QUESTION_FEEDBACK = 'question_feedback',
    ALTERNATIVE_FEEDBACK = 'alternative_feedback',
    RUBRICS_MARKING = 'rubrics_marking',
    INTERACTION_FEEDBACK = 'interaction_feedback',
}

export type ComposedQuickMarkOrComment = QuickMarkOrComment & QuickCommentsAppliedInfo;

export const commentsService = {
    getTitle(comment: CommentType, groups: any[]): string {
        const { data } = comment;
        const group = groups.find((group: any) => group.id === data.group);
        const questionNumber = data.questionNumber || '';
        let questionName = data?.document?.title || '';
        let tabName: string = urnService.getPaneFromUrn(data.uri);
        if (!commentsService.isAnnotationFromGrader(comment)) {
            tabName = 'submission';
            questionName = data.questionTitle;
        }
        const title = `${tl(`grading.tabNames.${tabName}`)} ${questionNumber}`;
        let channel = '';

        if (group && group.id) {
            const idCaption = group.id.split('-').join('');
            channel = ` / ${tl('grading.features.commentList.postedTo')} ${tl(`grading.annotations.channels.${idCaption}.name`)}`;
        }
        const noDataText = `${tl('grading.features.commentList.in')} ${title} ${channel}`;
        const dataText = `${questionNumber} ${questionName} ${channel}`;

        return !isNullOrUndefined(questionName) ? dataText : noDataText;
    },

    getDate(comment: CommentType): string {
        const { data } = comment;
        let date = '';
        if (data.created) {
            date = moment.utc(data.created).local().format('LLLL');
        }
        return date;
    },

    /* eslint-disable @typescript-eslint/camelcase, camelcase */
    getUserName(comment: CommentType): string {
        const { data } = comment;
        let name = '';
        if (data.user_info) {
            name = data.user_info.display_name;
        }
        return name;
    },

    getAllEvaluators() {
        const { assessment } = store.getState() as RootState;
        const { committees, evaluators } = assessment;
        if (committees) {
            let allEvaluators: Evaluator[] = [];
            committees.forEach((committee: Committee) => {
                allEvaluators = allEvaluators.concat(committee.evaluators || []);
            });

            return allEvaluators;
        }

        return evaluators;
    },

    getEvaluatorById(evaluatorId: number) {
        const allEvaluators = this.getAllEvaluators();
        return allEvaluators && allEvaluators.length > 0 && allEvaluators.find((evaluator: Evaluator) => {
            return evaluator.id === evaluatorId;
        });
    },

    parseQuickMarks(quickMarks: QuickMarkOrComment[]): any {
        const newList: ComposedQuickMarkOrComment[] = [];
        quickMarks.forEach((quickMarkORComment: QuickMarkOrComment) => {
            quickMarkORComment.appliedBy.forEach((appliedByInfo: QuickCommentsAppliedInfo) => {
                newList.push({
                    ...quickMarkORComment, ...appliedByInfo,
                });
            });
        });
        return newList;
    },

    createCommentFromQuickMarks(quickComment: QuickMarkOrComment): CustomQuickComment {
        const {
            quickMarkId, criteriaText, questionNumber, criteriaScore, evaluatorId, questionId, questionTitle,
        } = quickComment;
        return {
            evaluatorId,
            questionNumber,
            questionId,
            quickMarkId,
            criteriaText,
            criteriaScore,
            questionTitle,
            group: 'ch-individual-feedback',
            isQuickComment: true,
        };
    },

    parseCandidateScoreDetails: (questions: any): AlternativeOrQuestionFeedback[] | CriteriaComment[] | InteractionFeedback[] => {
        const newList: any[] = [];
        questions.forEach((question: any) => {
            const {
                type, questionId, questionNumber, questionTitle, selectedAlternative, responseOrderIndex,
            } = question;

            if (type === CommentTypes.CRITERIA_MARKING || type === CommentTypes.BANDED_MARKING || type === CommentTypes.RUBRICS_MARKING) {
                let criteria: any = [];
                if (type === CommentTypes.CRITERIA_MARKING) {
                    criteria = xmlParser.parseCriteriaMarks(question?.rubricBlock) as Criterion[];
                }

                if (type === CommentTypes.BANDED_MARKING || type === CommentTypes.RUBRICS_MARKING) {
                    criteria = xmlParser.parseBandedCriteriaMarks(question?.rubricBlock) as BandedCriterion[];
                }

                const appliedByGroupedByEvaluator = groupBy(question?.appliedBy, (appliedByIno: CriterionAppliedByInfo) => appliedByIno.evaluatorId);

                Object.keys(appliedByGroupedByEvaluator).forEach((evaluatorId: string) => {
                    newList.push({
                        type,
                        questionId,
                        questionNumber,
                        criteria,
                        questionTitle,
                        appliedBy: appliedByGroupedByEvaluator[evaluatorId],
                        appliedByEvaluatorId: evaluatorId,
                        group: 'ch-individual-feedback',
                    });
                });
            }

            if (type === CommentTypes.ALTERNATIVE_FEEDBACK || type === CommentTypes.QUESTION_FEEDBACK) {
                const feedback = xmlParser.parseQuestionOrAlternativeFeedback(question?.feedback, type);

                newList.push({
                    questionId,
                    questionNumber,
                    questionTitle,
                    feedback: feedback?.feedbackContent,
                    type,
                    selectedAlternative,
                    alternativeIdentifier: feedback?.alternativeIdentifier,
                    group: 'ch-individual-feedback',
                });
            }

            if (type === CommentTypes.INTERACTION_FEEDBACK) {
                // eslint-disable-next-line no-unused-expressions
                question?.feedback.forEach((outcome: any) => {
                    const feedback = xmlParser.parseInteractionFeedback(outcome, question);
                    if (!isNullOrUndefined(feedback?.feedbackContent)) {
                        newList.push({
                            questionId,
                            questionNumber,
                            feedback: feedback?.feedbackContent,
                            type,
                            outcomeIdentifier: feedback?.identifier,
                            responseOrderIndex,
                            group: 'ch-individual-feedback',
                        });
                    }
                });
            }
        });

        return newList;
    },

    isCriteriaComment: (comment: CommentType) => {
        return comment.data.type === CommentTypes.CRITERIA_MARKING;
    },

    isBandedCriteriaComment: (comment: CommentType) => {
        return comment.data.type === CommentTypes.BANDED_MARKING;
    },

    isRubricMarksComment: (comment: CommentType) => {
        return comment.data.type === CommentTypes.RUBRICS_MARKING;
    },

    isQuickComment: (comment: CommentType) => {
        return comment.data.isQuickComment;
    },

    isQuestionOrAlternativeOrInteractionFeedback: (comment: CommentType) => {
        return comment.data.type === CommentTypes.QUESTION_FEEDBACK
            || comment.data.type === CommentTypes.ALTERNATIVE_FEEDBACK
            || comment.data.type === CommentTypes.INTERACTION_FEEDBACK;
    },

    isAnnotationFromGrader: (comment: CommentType) => {
        return !commentsService.isBandedCriteriaComment(comment)
            && !commentsService.isQuickComment(comment)
            && !commentsService.isCriteriaComment(comment)
            && !commentsService.isQuestionOrAlternativeOrInteractionFeedback(comment)
            && !commentsService.isRubricMarksComment(comment);
    },
};
